import React from "react";
import {
  Badge,
  Button,
  Box,
  Tooltip,
  IconButton,
  Popper,
  Paper,
  ClickAwayListener,
  Typography,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { GridFilterModel, GridLinkOperator } from "@mui/x-data-grid-pro";
import {
  AppFilterSettings,
  AppGridId,
  AppGridSetting,
  GridIdsToSave,
  MaxSavedFilters,
} from "../../models/app/AppGridModels";
import { AppFilterItem } from "../../models/app/AppModels";
import AppConfirmDialog from "./AppConfirmDialog";
import {
  getUniqueNameValidation,
  isInvalidFilterNameToken,
} from "../../services/utilities/utility";
import { addGridSetting } from "../../redux/reducer/features/App/AppGridSlice";
import axios from "axios";
import { baseURL } from "../../api/agent";

export interface ManageSavedFilterProps {
  savedFilters?: AppFilterSettings[];
  onApplySavedFilter?: (filter: AppFilterSettings) => void;
  onDeleteSavedFilter?: (filterName: string) => void;
  onRenameSavedFilter?: (oldName: string, newName: string) => void;
}

interface useSavedFilterParams {
  gridId?: AppGridId;
  gridSettings: AppGridSetting[];
  dispatch: any;
  saveSettingMutation: (args: any) => void;
  userId?: string;
  paging: any;
  setPaging: (value: any) => void;
  filterModel: GridFilterModel;
  setFilterModel: (model: GridFilterModel) => void;
  setSnackbar: (value: {
    open: boolean;
    message: string | React.ReactNode;
    severity: "success" | "error" | "warning" | "info";
  }) => void;
  onNewShortcutCreated?: (filterName: string) => void;
}

type FilterItemInput = Partial<AppFilterItem> & {
  id?: number | string;
  value?: unknown;
};

type SavedFilterInput = Pick<
  AppFilterSettings,
  "filterShortcutName" | "isShortcut" | "description" | "imageUri"
> & {
  items: FilterItemInput[];
  logicalOperator?: AppFilterSettings["logicalOperator"] | GridLinkOperator;
};

export const useSavedFilterHandlers = ({
  gridId,
  gridSettings,
  dispatch,
  saveSettingMutation,
  userId,
  paging,
  setPaging,
  filterModel,
  setFilterModel,
  setSnackbar,
  onNewShortcutCreated,
}: useSavedFilterParams) => {
  // Finds the current grid's persisted setting from Redux state.
  const getGridSetting = (): AppGridSetting | undefined => {
    if (gridId === undefined) return undefined;
    return gridSettings.find((setting) => setting.gridId === gridId);
  };

  // Normalizes filter items into the exact AppFilterItem shape we persist.
  const normalizeFilterItems = (items: FilterItemInput[]): AppFilterItem[] => {
    return items.map((item, index) => ({
      id: typeof item.id === "number" ? item.id : index,
      columnField: item.columnField ?? "",
      operatorValue: item.operatorValue ?? "",
      value:
        (item.value as any)?.constructor === Date
          ? (item.value as any).toISOString()
          : item.value == null
            ? ""
            : String(item.value),
    }));
  };

  // Returns saved filters for the current grid; always returns an array.
  const getSavedFilters = (): AppFilterSettings[] => {
    const setting = getGridSetting();
    const filters = setting?.filterSettings;
    if (!filters) return [];
    return Array.isArray(filters) ? filters : [filters];
  };

  // Returns only filter names for duplicate name validation.
  const getExistingFilterNames = (): string[] => {
    return getSavedFilters().map((filter) => filter.filterShortcutName || "");
  };

  // Wrapper around parent snackbar setter for consistent messages.
  const showSnackbar = (
    message: string | React.ReactNode,
    severity: "success" | "error" | "warning" | "info",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  // Builds one saved filter object in the format we store in Redux/DB.
  const buildSavedFilter = (input: SavedFilterInput): AppFilterSettings => {
    return {
      items: normalizeFilterItems(input.items),
      logicalOperator:
        input.logicalOperator === "or" ||
        input.logicalOperator === GridLinkOperator.Or
          ? "or"
          : "and",
      filterShortcutName: input.filterShortcutName ?? "",
      isShortcut: !!input.isShortcut,
      description: input.description?.trim() ?? "",
      imageUri: input.imageUri,
    };
  };

  // One save pipeline: update Redux first, then persist the same payload to DB.
  const persistFilterSettings = (filters: AppFilterSettings[]) => {
    if (gridId === undefined) return;

    const currentSetting = getGridSetting();
    const normalizedFilters = filters.map((filter) =>
      buildSavedFilter({
        items: filter.items,
        logicalOperator: filter.logicalOperator,
        filterShortcutName: filter.filterShortcutName,
        isShortcut: filter.isShortcut,
        description: filter.description,
        imageUri: filter.imageUri,
      }),
    );

    dispatch(
      addGridSetting({
        setting: {
          gridId,
          filterSettings: normalizedFilters,
        },
      }),
    );

    if (!GridIdsToSave.includes(gridId)) return;

    const settingPayload = {
      columnVisibilityModel: currentSetting?.columnVisibilityModel,
      filterSettings: normalizedFilters,
    };

    saveSettingMutation({
      data: {
        userId: userId ?? "",
        gridId,
        setting: JSON.stringify(settingPayload),
      },
    });
  };

  // Applies a selected saved filter to grid filter model and paging state.
  const handleApplySavedFilter = (filter: AppFilterSettings) => {
    const logicalOperator = filter.logicalOperator === "or" ? "or" : "and";
    const newFilterModel: GridFilterModel = {
      items: normalizeFilterItems(filter.items),
      quickFilterValues: filter.quickFilterValues || [],
      linkOperator:
        logicalOperator === "or" ? GridLinkOperator.Or : GridLinkOperator.And,
    };

    setFilterModel(newFilterModel);
    setPaging({
      ...paging,
      pageIndex: 0,
      items: filter.items || [],
      quickFilterValues: filter.quickFilterValues || [],
      logicalOperator,
    });

    showSnackbar(
      <>
        Filter <b>{filter.filterShortcutName}</b> applied successfully!
      </>,
      "success",
    );
  };

  // Creates and saves a new filter after running basic validations.
  const handleSaveFilter = async (
    filterName: string,
    isShortcut: boolean,
    description?: string,
    image?: File,
  ) => {
    if (gridId === undefined) {
      showSnackbar("Cannot save filter: Grid ID is not defined", "error");
      return;
    }

    const existingFilters = getSavedFilters();
    if (existingFilters.length >= MaxSavedFilters) {
      showSnackbar(
        `Maximum limit of ${MaxSavedFilters} saved filters reached. Please delete a filter before adding a new one.`,
        "warning",
      );
      return;
    }

    let imageUri: string | undefined;

    if (isShortcut && image) {
      try {
        const formData = new FormData();
        formData.append("image", image);

        const response = await axios.post(
          `${baseURL}api/TargetWebSetting/UploadImage`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (response.status === 200 && response.data?.entity?.absoluteUrl) {
          imageUri = response.data.entity.absoluteUrl;
        } else {
          showSnackbar(
            "Image upload failed. Filter saved without image.",
            "warning",
          );
        }
      } catch {
        showSnackbar(
          "Image upload failed. Filter saved without image.",
          "warning",
        );
      }
    }

    const newFilter = buildSavedFilter({
      items: filterModel.items,
      logicalOperator: filterModel.linkOperator,
      filterShortcutName: filterName,
      isShortcut,
      description,
      imageUri,
    });

    persistFilterSettings([...existingFilters, newFilter]);
    if (isShortcut) onNewShortcutCreated?.(filterName);
    showSnackbar(
      <>
        Filter <b>{filterName}</b> saved successfully!
      </>,
      "success",
    );
  };

  // Deletes a saved filter by name and persists the updated list.
  const handleDeleteSavedFilter = (filterName: string) => {
    if (gridId === undefined) return;

    const updatedFilters = getSavedFilters().filter(
      (filter) => filter.filterShortcutName !== filterName,
    );

    persistFilterSettings(updatedFilters);
    showSnackbar(
      <>
        Filter <b>{filterName}</b> deleted successfully!
      </>,
      "success",
    );
  };

  // Renames a saved filter and persists the updated list.
  const handleRenameSavedFilter = (oldName: string, newName: string) => {
    if (gridId === undefined) return;

    const updatedFilters = getSavedFilters().map((filter) =>
      filter.filterShortcutName === oldName
        ? { ...filter, filterShortcutName: newName }
        : filter,
    );

    persistFilterSettings(updatedFilters);
    showSnackbar(
      <>
        Filter renamed to <b>{newName}</b> successfully!
      </>,
      "success",
    );
  };

  return {
    getExistingFilterNames,
    getSavedFilters,
    persistFilterSettings,
    handleApplySavedFilter,
    handleDeleteSavedFilter,
    handleRenameSavedFilter,
    handleSaveFilter,
  };
};

const ManageSavedFilters: React.FC<ManageSavedFilterProps> = ({
  savedFilters,
  onApplySavedFilter,
  onDeleteSavedFilter,
  onRenameSavedFilter,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const openPopper = Boolean(anchorEl);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState<string>("");
  const [newFilterName, setNewFilterName] = React.useState<string>("");
  const existingFilterNames = React.useMemo(
    () => (savedFilters || []).map((filter) => filter.filterShortcutName || ""),
    [savedFilters],
  );
  const renameValidation = getUniqueNameValidation({
    name: newFilterName,
    existingNames: existingFilterNames,
    excludeName: selectedFilter,
  });
  const isInvalidRenameToken = isInvalidFilterNameToken(newFilterName);

  // Opens the saved filters popper anchored to the toolbar button.
  const handleOpenPopper = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Closes the saved filters popper.
  const handleClosePopper = () => {
    setAnchorEl(null);
  };

  // Applies a filter and closes the popper for better UX.
  const handleApplyFilter = (filter: AppFilterSettings) => {
    onApplySavedFilter?.(filter);
    handleClosePopper();
  };

  // Opens delete confirmation dialog for the selected filter.
  const handleDeleteClick = (filterName: string) => {
    setSelectedFilter(filterName);
    setDeleteDialogOpen(true);
  };

  // Confirms delete action and resets dialog state.
  const handleConfirmDelete = () => {
    onDeleteSavedFilter?.(selectedFilter);
    setDeleteDialogOpen(false);
    setSelectedFilter("");
  };

  // Cancels delete action and resets dialog state.
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedFilter("");
  };

  // Opens rename dialog and pre-fills current filter name.
  const handleRenameClick = (filter: AppFilterSettings) => {
    setSelectedFilter(filter.filterShortcutName || "");
    setNewFilterName(filter.filterShortcutName || "");
    setRenameDialogOpen(true);
  };

  // Confirms rename if valid and resets dialog state.
  const handleConfirmRename = () => {
    if (
      newFilterName.trim() &&
      !isInvalidRenameToken &&
      newFilterName !== selectedFilter &&
      !renameValidation.isDuplicate
    ) {
      onRenameSavedFilter?.(selectedFilter, newFilterName.trim());
    }
    setRenameDialogOpen(false);
    setSelectedFilter("");
    setNewFilterName("");
  };

  // Cancels rename action and resets dialog state.
  const handleCancelRename = () => {
    setRenameDialogOpen(false);
    setSelectedFilter("");
    setNewFilterName("");
  };

  // Handles keyboard interactions inside the popper.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      handleClosePopper();
    }
  };

  return (
    <>
      <Tooltip title="Saved Filters">
        <Button
          ref={buttonRef}
          size="small"
          startIcon={
            <Badge
              badgeContent={savedFilters?.length || 0}
              color="primary"
              invisible={!savedFilters || savedFilters.length === 0}
            >
              <FilterListIcon />
            </Badge>
          }
          onClick={handleOpenPopper}
          disabled={!savedFilters || savedFilters.length === 0}
        >
          Saved Filters
        </Button>
      </Tooltip>

      <Popper
        open={openPopper}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        onKeyDown={handleKeyDown}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClosePopper}>
          <Paper
            elevation={8}
            sx={{
              maxHeight: 450,
              width: 450,
              overflow: "auto",
              mt: 1,
            }}
          >
            {savedFilters && savedFilters.length > 0 ? (
              <Box>
                {/* Header */}
                <Box sx={{ p: 2, bgcolor: "action.hover" }}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <FilterListIcon fontSize="small" />
                      Manage Filters
                    </Typography>
                    <Chip
                      label={`${savedFilters.length} Total`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
                <Divider />

                {/* Filter Cards */}
                <Box sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    {savedFilters.map((filter, index) => (
                      <Card
                        key={`filter-${index}`}
                        variant="outlined"
                        sx={{
                          "&:hover": {
                            boxShadow: 2,
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Stack
                            direction="row"
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 1.5, flex: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body1"
                                  fontWeight="medium"
                                  gutterBottom
                                >
                                  {filter.filterShortcutName &&
                                  filter.filterShortcutName !== "null" &&
                                  String(filter.filterShortcutName).trim()
                                    ? filter.filterShortcutName
                                    : "Unnamed Filter"}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                  <Chip
                                    label={`${filter.items?.length || 0} Filters`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  {filter.isShortcut && (
                                    <Chip
                                      label="Shortcut"
                                      size="small"
                                      color="default"
                                      variant="filled"
                                    />
                                  )}
                                </Stack>
                              </Box>
                              <Stack
                                direction="row"
                                sx={{
                                  "& .MuiIconButton-root:hover": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                              >
                                <Tooltip title="Rename">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleRenameClick(filter)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <IconButton size="small">|</IconButton>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      handleDeleteClick(
                                        filter.filterShortcutName || "",
                                      )
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <IconButton size="small">|</IconButton>
                                <Tooltip title="Apply">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApplyFilter(filter)}
                                  >
                                    <FilterAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <FilterListIcon
                  sx={{ fontSize: 64, color: "action.disabled", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No saved filters yet
                </Typography>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      <AppConfirmDialog
        isOpen={deleteDialogOpen}
        handleClose={handleCancelDelete}
        onFail={handleCancelDelete}
        onSuccess={handleConfirmDelete}
        headerText="Delete Filter"
        bodyText={
          <>
            Are you sure you want to delete the filter <b>{selectedFilter}</b>?
            This action cannot be undone.
          </>
        }
      />

      <Dialog
        open={renameDialogOpen}
        onClose={handleCancelRename}
        aria-labelledby="rename-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="rename-dialog-title">Rename Filter</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Filter Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newFilterName}
            onChange={(e) => setNewFilterName(e.target.value)}
            sx={{ mt: 1 }}
            required
            error={isInvalidRenameToken || renameValidation.showDuplicateError}
            helperText={
              isInvalidRenameToken
                ? 'Filter name cannot be null, "", or \"\".'
                : renameValidation.helperText
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCancelRename}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRename}
            color="primary"
            variant="contained"
            disabled={
              !newFilterName.trim() ||
              isInvalidRenameToken ||
              renameValidation.isDuplicate
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageSavedFilters;
